import { DocumentNode, useMutation } from "@apollo/client";
import {
	Contact,
	ContactType,
	DocumentStatus,
	EmailInformation,
	Project,
	ProjectType,
	SalutationType,
	DocumentType,
} from "@repo/server-api";
import { ContactPersonInformation } from "@repo/server-api/types";
import { IdWithName } from "@repo/shared/common";
import dayjs, { Dayjs } from "dayjs";
import { parse, toSeconds } from "iso8601-duration";
import { CommercialDocumentSummary } from "@repo/shared-invoice/types/CommercialDocumentTypes";

type IdWithNameWithEmail = IdWithName & { email: string };

export const validErrorState = (errors: Record<string, boolean | object | string>) => {
	if (!errors) return true;
	return Object.keys(errors).every((key) => {
		if (typeof errors[key] === "object") {
			return Object.keys(errors[key]).every((subKey) => {
				if (typeof (errors[key] as Record<string, any>)[subKey] === "object") {
					return Object.keys((errors[key] as Record<string, any>)[subKey]).every((subSubKey) => {
						if (typeof (errors[key] as Record<string, any>)[subKey][subSubKey] === "object") {
							return Object.keys((errors[key] as Record<string, any>)[subKey][subSubKey]).every(
								(subSubSubKey) =>
									!(errors[key] as Record<string, any>)[subKey][subSubKey][subSubSubKey],
							);
						} else {
							return !(errors[key] as Record<string, any>)[subKey][subSubKey];
						}
					});
				} else {
					return !(errors[key] as Record<string, any>)[subKey];
				}
			});
		} else {
			return !errors[key];
		}
	});
};

export const errorStateHasMessages = (errors: Record<string, boolean | object | string>) => {
	if (!errors) return false;
	return Object.keys(errors).some((key) => {
		if (typeof errors[key] === "object") {
			return Object.keys(errors[key] as object).some((subKey) => {
				if (typeof (errors[key] as Record<string, any>)[subKey] === "object") {
					return Object.keys((errors[key] as Record<string, any>)[subKey]).some((subSubKey) => {
						if (typeof (errors[key] as Record<string, any>)[subKey][subSubKey] === "object") {
							return Object.keys((errors[key] as Record<string, any>)[subKey][subSubKey]).some(
								(subSubSubKey) =>
									(errors[key] as Record<string, any>)[subKey][subSubKey][subSubSubKey],
							);
						} else {
							return (errors[key] as Record<string, any>)[subKey][subSubKey];
						}
					});
				} else {
					return (errors[key] as Record<string, any>)[subKey];
				}
			});
		} else {
			return errors[key];
		}
	});
};

export const parseContactPersonIDs = (contact: Contact | null) => {
	if (!contact) {
		return [];
	}
	const res: string[] = [];
	contact.additionalInformation?.forEach((info) => {
		if ((info as ContactPersonInformation).__typename === "ContactPersonInformation") {
			if ((info as ContactPersonInformation).contactPersonId) {
				res.push((info as ContactPersonInformation).contactPersonId);
			}
		}
	});
	return res;
};

export const useMutationWithPromise = (mutation: DocumentNode) => {
	const [mutate] = useMutation(mutation);
	return (variables: object) => {
		return new Promise((resolve, reject) => {
			mutate({
				variables,
				onCompleted: resolve,
				onError: reject,
			});
		});
	};
};

export const emptyContact: Contact = {
	id: "",
	companyName: "",
	contactNumber: "",
	contactTypes: [],
	firstName: "",
	lastName: "",
	additionalInformation: [],
	displayContactType: [],
	displayName: "",
	isACompany: false,
	isAFreelancer: false,
	isAPerson: false,
	isDeleted: false,
	isTemporary: false,
	isContactPerson: false,
	isEmployee: false,
	isAMailbox: false,
	isMemberOf: [],
	version: 0,
	salutation: SalutationType.Mr,
	shortCode: "",
	title: "",
	__typename: "Contact",
};

export const setContactValues: (contact: Contact | null) => Contact = (contact) => {
	return {
		...emptyContact,
		...contact,
	};
};

export const emptyProject: Project = {
	id: "",
	isDeleted: false,
	isRoot: false,
	isGroup: false,
	name: "",
	number: "",
	status: DocumentStatus.Draft,
	path: [],
	projectType: ProjectType.Project,
	parentProjectId: null,
	additionalInformation: [],
	range: {
		contains: false,
		dates: [],
		from: "",
		to: "",
	},
	budget: {
		amount: 0,
		hasAmountLimit: false,
		hasTimeLimit: false,
		hours: 0,
	},
	saleableServices: [],
	saleableServiceGroups: [],
	saleableUnits: [],
	createdBy: { ...emptyContact, id: "" },
	createdById: "",
	timestamp: "",
	__typename: "Project",
};

export const setProjectValues: (project: Project | null) => Project = (project) => {
	return {
		...emptyProject,
		...project,
	};
};

export const formatDate = (date: Date | string | null | dayjs.Dayjs, lang: string): string => {
	if (!date) return "";
	if (lang === "de") {
		return dayjs(date).format("DD.MM.YYYY");
	}
	return dayjs(date).format("MM/DD/YYYY");
};

export const formatDateTime = (date: Date | string, lang: string): string => {
	if (lang === "de") {
		return dayjs(date).format("DD.MM.YYYY HH:mm");
	}
	return dayjs(date).format("MM/DD/YYYY HH:mm");
};

export const formatHoursMinutes = (value: number): string => {
	if (value === null) return "0,00 h";
	const hours = value / 60;
	const [intPart, decPart] = hours.toFixed(2).split(".");

	const intWithDots = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

	return `${intWithDots},${decPart} h`;
};

type typeForDeepEqual =
	| ContactType
	| string
	| number
	| boolean
	| null
	| Array<typeForDeepEqual>
	| { [key: string]: typeForDeepEqual };

export const deepEqual = (x: typeForDeepEqual, y: typeForDeepEqual): boolean => {
	return x && y && typeof x === "object" && typeof y === "object"
		? Object.keys(x).length === Object.keys(y).length &&
				Object.keys(x).reduce((isEqual: boolean, key: string): boolean => {
					if (Array.isArray(x) || Array.isArray(y)) {
						// If both are arrays, compare by index
						return isEqual;
					} else {
						// Both are objects, safe to index by string key
						return (
							isEqual &&
							deepEqual(
								(x as { [key: string]: typeForDeepEqual })[key],
								(y as { [key: string]: typeForDeepEqual })[key],
							)
						);
					}
				}, true)
		: x === y;
};

export const formatTimeSpan = (time: number | null | undefined): string | null => {
	if (time === null || time === undefined) {
		return null;
	}

	const isNegative = time < 0;
	// time is 1 = 1 minute
	time = Math.abs(time);
	if (time === 0 || isNaN(time)) {
		return "PT0H0M";
	}
	const hours = Math.floor(time / 60);
	const minutes = Math.ceil(time % 60);

	return (isNegative ? "-" : "") + `PT${hours}H${minutes}M`;
};

export const deformatTimeSpan = (time: string | null): number | null => {
	if (!time) {
		return null;
	}

	const isNegative = time.startsWith("-");
	const res = Math.ceil(toSeconds(parse(time)) / 60);
	return isNegative ? -1 * res : res; // Convert to minutes
};

export { formatDurationToHHmm as formatTimeSpanToHHmm } from "@repo/shared/common";

export const formatDateTimeForApi = (date: Date | string): string => {
	return dayjs(date).utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
};

export const normalizeContact = (c: Contact | null): IdWithName | IdWithNameWithEmail | null => {
	if (!c) return null;

	const emailInfo = c.additionalInformation?.find((info) =>
		(info as EmailInformation).__typename === "EmailInformation"
			? Boolean((info as EmailInformation).emailAddress)
			: false,
	) as EmailInformation | undefined;

	return emailInfo
		? {
				id: c.id,
				name: c.displayName,
				email: emailInfo.emailAddress,
			}
		: {
				id: c.id,
				name: c.displayName,
			};
};

export const normalizeProject = (p: Project | null): IdWithName | null => {
	if (!p) return null;

	return {
		id: p.id,
		name: p.name,
	};
};

/**
 * Formats a datetime string as UTC without timezone conversion.
 * If input is "2025-12-27 08:00", output will be "2025-12-27T08:00:00Z" (not converted from local timezone).
 * This is useful for time tracking where times should be treated as absolute values, not relative to a timezone.
 */
export const formatDateTimeAsUtc = (date: Date | string | Dayjs): string => {
	const d = dayjs.utc(date);
	// Extract components and format as UTC without conversion
	return d.format("YYYY-MM-DD") + "T" + d.format("HH:mm:ss") + "Z";
};

export const findExactSingleContactType = (contact: Contact | null): ContactType | null => {
	if (!contact || contact.contactTypes.length === 0) {
		return null;
	}
	const types = [
		ContactType.Client,
		ContactType.Supplier,
		ContactType.Freelancer,
		ContactType.Contractor,
		ContactType.Employee,
		ContactType.ContactPerson,
	];
	let validTypeForQuery = null;
	contact.contactTypes.forEach((type) => {
		if (types.includes(type)) {
			validTypeForQuery = type;
		}
	});

	return validTypeForQuery;
};

export const truncateText = (text: string, length: number): string => {
	if (text.length <= length) {
		return text;
	}
	return text.slice(0, length) + "\u2026";
};

export const formatCurrencyNumber = (value: number | null): string => {
	if (value === null) return "€ 0,00";
	const [intPart, decPart] = value.toFixed(2).split(".");

	const intWithDots = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

	return `€ ${intWithDots},${decPart}`;
};

export const formatNumber = (value: number | null): string => {
	if (value === null) return "0,00";
	const [intPart, decPart] = value.toFixed(2).split(".");

	const intWithDots = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

	return `${intWithDots},${decPart}`;
};

export const getDocumentStatusesForFilter = (status: string): DocumentStatus[] => {
	const upperStatus = status.toLocaleUpperCase();

	switch (upperStatus) {
		case "UNPAID":
			return [DocumentStatus.Sent, DocumentStatus.PartiallyPaid, DocumentStatus.Finalized];
		case "UNSENT":
			return [DocumentStatus.Finalized, DocumentStatus.PartiallyPaid, DocumentStatus.Overdue];
		case "DRAFT":
			return [DocumentStatus.Draft];
		case "OVERDUE":
			return [DocumentStatus.Overdue];
		case "PAID":
			return [DocumentStatus.Paid];
		case "CANCELLED":
			return [DocumentStatus.Cancelled];
		case "OPEN":
			return [DocumentStatus.Sent];
		case "ACCEPTED":
			return [DocumentStatus.Accepted];
		case "REJECTED":
			return [DocumentStatus.Rejected];
		default:
			return [DocumentStatus.Draft];
	}
};

export const canGenerateReminder = (document: CommercialDocumentSummary): boolean => {
	if (!document) return false;

	const { status, sentDate, type } = document;

	// Only invoices can have reminders generated
	if (type !== DocumentType.Invoice) {
		return false;
	}

	const acceptedStatuses = [DocumentStatus.Overdue];

	if (!acceptedStatuses.includes(status)) {
		return false;
	}

	// If the document has not been sent, a reminder cannot be generated
	if (!sentDate) {
		return false;
	}

	return true;
};
